/**
 * <s-moschus-offer> — once-per-session add-on offer on the way to checkout.
 *
 * Intercepts checkout submits anywhere on the page:
 *   - cart drawer / cart page: submitter name="checkout"
 *   - product page "Jetzt zum Checkout": submitter name="return_to" with a checkout value
 * and opens the offer dialog instead. The shopper's original submit is replayed natively
 * when they decline, so cart updates, line properties and return_to behave exactly as before.
 *
 * Commands (on this element via commandfor):
 *   --accept  add the offer product (plus the product-page item for "Jetzt zum Checkout"),
 *             then go to checkout through /discount/<code> so the offer price applies
 *   --decline continue to checkout unchanged
 *
 * Data attributes (written by Liquid): variant-id, handle, cart-url, cart-add-url,
 * checkout-url, discount-url, added-label.
 */
const SEEN_KEY = 'fiore-moschus-offer-seen';

class SMoschusOffer extends HTMLElement {
  #ac;
  #pending = null;
  #replaying = false;
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    // Capture phase on the document runs before any form's own listeners and before navigation.
    document.addEventListener('submit', (event) => this.#onSubmit(event), { ...opts, capture: true });

    this.addEventListener('command', (event) => {
      if (event.command === '--accept') {
        event.preventDefault();
        this.#accept();
      } else if (event.command === '--decline') {
        event.preventDefault();
        this.#decline();
      }
    }, opts);

    this.#dialog?.addEventListener('close', () => {
      if (!this.#busy) this.#pending = null;
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #dialog() {
    return this.querySelector('dialog');
  }

  #seen() {
    try {
      return sessionStorage.getItem(SEEN_KEY) === '1';
    } catch {
      return false;
    }
  }

  #markSeen() {
    try {
      sessionStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* storage unavailable: the offer may show again, which is harmless */
    }
  }

  #isCheckoutSubmitter(submitter) {
    if (!(submitter instanceof HTMLElement)) return false;
    const name = submitter.getAttribute('name');
    if (name === 'checkout') return true;
    return name === 'return_to' && (submitter.getAttribute('value') || '').includes('checkout');
  }

  async #onSubmit(event) {
    if (this.#replaying || event.defaultPrevented) return;

    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || !this.#isCheckoutSubmitter(submitter)) return;
    if (this.#seen()) return;

    // Buying the offer product itself straight to checkout needs no offer.
    const idField = form.elements.namedItem('id');
    if (submitter.getAttribute('name') === 'return_to' && idField && String(idField.value) === this.dataset.variantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#pending = { form, submitter };

    if (await this.#cartHasOffer()) {
      this.#replay();
      return;
    }

    this.#markSeen();
    this.#dialog?.showModal();
  }

  async #cartHasOffer() {
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const cart = await response.json();
      return (cart.items || []).some(
        (item) => String(item.variant_id) === this.dataset.variantId || item.handle === this.dataset.handle
      );
    } catch {
      return false;
    }
  }

  async #accept() {
    if (this.#busy) return;
    const pending = this.#pending;
    this.#busy = true;

    const button = this.querySelector('[data-moschus-accept]');
    button?.setAttribute('aria-disabled', 'true');
    button?.classList.add('opacity-50', 'cursor-wait');

    try {
      const addUrl = `${this.dataset.cartAddUrl}.js`;

      // "Jetzt zum Checkout" has not added its product yet: post the form exactly as the browser would (without return_to).
      if (pending && pending.submitter.getAttribute('name') === 'return_to') {
        const productResponse = await fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(pending.form),
        });
        if (!productResponse.ok) throw new Error('product add failed');
      }

      const offerResponse = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(this.dataset.variantId), quantity: 1 }] }),
      });
      if (!offerResponse.ok) throw new Error('offer add failed');

      if (button && this.dataset.addedLabel) button.textContent = this.dataset.addedLabel;
      window.location.assign(this.dataset.discountUrl);
    } catch {
      // Never block the purchase: fall back to the shopper's original checkout step.
      this.#busy = false;
      button?.removeAttribute('aria-disabled');
      button?.classList.remove('opacity-50', 'cursor-wait');
      this.#decline();
    }
  }

  #decline() {
    this.#busy = true;
    this.#replay();
  }

  #replay() {
    const pending = this.#pending;
    if (this.#dialog?.open) this.#dialog.close();

    if (!pending || !pending.form.isConnected) {
      window.location.assign(this.dataset.checkoutUrl);
      return;
    }

    this.#replaying = true;
    if (typeof pending.form.requestSubmit === 'function') {
      pending.form.requestSubmit(pending.submitter);
    } else {
      pending.submitter.click();
    }
  }
}

if (!customElements.get('s-moschus-offer')) customElements.define('s-moschus-offer', SMoschusOffer);
